import { useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Text, Button, Colors, Icon } from '@mercator-gcp/grout';
import SearchInput from 'components/elements/SearchInput';
import { Spinner } from 'components/elements/Spinner';
import Tooltip from 'components/elements/Tooltip';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import ButtonExportCollectionToCSV from 'components/widgets/Dashboard/ButtonExportCollectionToCSV';
import DashboardLeadsTable from 'components/widgets/Dashboard/DashboardLeadsTable';
import EmptyGroupPlaceholder from 'components/widgets/Dashboard/EmptyGroupPlaceholder';
import { useCollectionsShared } from 'hooks/useCollections';
import { useDebounce } from 'hooks/useDebounced';
import { useFilterCollectionProjects } from 'hooks/useFilterCollectionProjects';
import { usePaginate } from 'hooks/usePaginate';
import { useSortedProjects } from 'hooks/useSortedProjects';
import { useSelectedSharedCollectionQuery } from 'state/queryParams';
import { trackExportProjectList } from 'utils/analytics';
import {
  ExportProjectListGroupTypes,
  ExportProjectListSuccessStates,
  ProjectGroupTypes,
} from 'utils/analytics/constants';
import { PAGINATED_PROJECT_LIMIT } from 'utils/constants';
import { toMillion } from 'utils/currency';
import { formatInteger } from 'utils/format';

const containerStyle = {
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  gap: 'padding_16',
};

const content = {
  container: {
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    paddingBottom: '54px',
  },
  parentColumn: {
    zIndex: 1,
    gridArea: 'b',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
  },
  parentRow: {
    justifyContent: 'space-between',
    justifyItems: 'left',
    alignItems: 'center',
    top: 0,
    zIndex: 3,
    padding: 0,
    gridGap: 3,
    width: '100%',
  },
};

const listOwnerText = {
  color: Colors.TextQuiet,
  paddingLeft: 'padding_16',
};

const sharedWithCounterText = {
  paddingLeft: 'padding_4',
  color: Colors.Black800,
};

const userCountRow = {
  justifyContent: 'flex-end',
};

const pipelineSearchRow = {
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};

const searchInputStyle = {
  maxWidth: '240px',
  flex: 1,
};

const CollectionDetails = () => {
  const [selectedGroup] = useSelectedSharedCollectionQuery();
  const { result: sharedList } = useCollectionsShared();
  const [keywordFilter, setKeywordFilter] = useState('');
  const debouncedKeywordFilter = useDebounce(keywordFilter, 500);
  const { nextPage, previousPage, page: currentPage, setPageNumber } = usePaginate();
  const { data: collectionProjects, isLoading } = useFilterCollectionProjects({
    id: Number(selectedGroup),
    page: currentPage,
    keywords: debouncedKeywordFilter,
  });
  const { sortedProjects } = useSortedProjects(collectionProjects?.results || [], true);
  const filteredCollections = sharedList?.data.filter(
    ({ id }) => id === Number(selectedGroup),
  )[0];

  const totalCost = useMemo(() => {
    const cost = collectionProjects?.aggregations?.total_cost.value || 0;

    const estimated_cost =
      collectionProjects?.aggregations?.total_estimated_cost.value || 0;

    return cost + estimated_cost;
  }, [
    collectionProjects?.aggregations?.total_cost.value,
    collectionProjects?.aggregations?.total_estimated_cost.value,
  ]);

  const totalPages = useMemo(() => {
    return collectionProjects?.count
      ? Math.ceil(collectionProjects.count / PAGINATED_PROJECT_LIMIT) || 1
      : 1;
  }, [collectionProjects?.count, PAGINATED_PROJECT_LIMIT]);

  if (!selectedGroup) {
    return (
      <EmptyGroupPlaceholder
        label="Shared lead lists will appear here"
        imageAlt="No pinned projects"
        imageUrl="/dashboard-group-empty-state.png"
      />
    );
  }

  if (isLoading) {
    return (
      <FlexColumn sx={containerStyle}>
        <Spinner />
      </FlexColumn>
    );
  }

  return (
    <FlexColumn sx={containerStyle}>
      <FlexRow sx={content.parentRow}>
        <Text.H3>{filteredCollections?.name}</Text.H3>
        <FlexRow.Right sx={{ gap: '8px' }}>
          <ButtonExportCollectionToCSV
            collectionId={Number(selectedGroup)}
            onMutate={(clickText) => {
              trackExportProjectList({
                groupId: Number(selectedGroup),
                groupType: ExportProjectListGroupTypes.SHARED_COLLECTION,
                clickText,
                successState: ExportProjectListSuccessStates.SENT,
              });
            }}
            onSuccess={(data) => {
              trackExportProjectList({
                groupId: Number(selectedGroup),
                groupType: ExportProjectListGroupTypes.SHARED_COLLECTION,
                linkPath: data || undefined,
                successState: ExportProjectListSuccessStates.SUCCESS,
              });
            }}
          />
          <Tooltip
            tooltipText="Projects are added by the Owner.
              You’re able to change project status for the Owner.
              Across the app, these project statues are only visible to the Owner."
            placement="bottom"
          >
            <Button
              variant="functional"
              startAdornment={<Icon name={Icon.Name.User} fill={Colors.Black800} />}
            >
              <Text.SmallLabel sx={sharedWithCounterText}>
                Shared with {filteredCollections?.num_shared_with} user
                {filteredCollections?.num_shared_with === 1 ? '' : 's'}
              </Text.SmallLabel>
            </Button>
          </Tooltip>
        </FlexRow.Right>
      </FlexRow>
      <FlexRow sx={userCountRow}>
        <Text sx={listOwnerText}>
          {filteredCollections?.shared_by.email} shared this list with you.
        </Text>
      </FlexRow>
      <FlexRow sx={pipelineSearchRow}>
        <Box>
          <Box>
            <Text.SmallLabel>Total Pipeline Potential</Text.SmallLabel>
          </Box>
          <Box>
            <Text.Lead>{totalCost ? toMillion(totalCost) : '-'}</Text.Lead>
          </Box>
          <Box>
            <Text.Body color={Colors.TextQuiet}>predicted project value</Text.Body>
          </Box>
        </Box>
        <Box sx={searchInputStyle}>
          <SearchInput
            value={keywordFilter}
            onChange={setKeywordFilter}
            placeholder="Search projects by keyword"
          />
        </Box>
      </FlexRow>
      <DashboardLeadsTable
        items={sortedProjects || []}
        leadContext={{
          groupId: selectedGroup,
          groupType: ProjectGroupTypes.SHARED_COLLECTION,
        }}
        header={`${formatInteger(filteredCollections?.num_projects)} active projects found`}
        footer={
          <Pagination
            currentPage={currentPage}
            isLoading={isLoading}
            setPage={(pageNumber) => setPageNumber(pageNumber)}
            totalPages={totalPages}
            onNext={() => nextPage()}
            onPrev={() => previousPage()}
          />
        }
      />
    </FlexColumn>
  );
};

export default CollectionDetails;
